import {
  AlertTriangle,
  Calculator,
  CircleDollarSign,
  Clock,
  Fuel,
  Lock,
  Pause,
  Play,
  Printer,
  Receipt,
  RefreshCw,
  Settings2,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SectionCard } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { mostrarPdfAutenticado } from '@/lib/descargas';
import { formatCurrency, formatDateTimeValue, formatNumber } from '@/lib/utils';

import { TurnoAjustesDialog } from '@/features/reportes/components/turno-ajustes-dialog';
import { TurnoCalculosDialog } from '@/features/reportes/components/turno-calculos-dialog';
import { usePermisosLiquidacion } from '@/features/reportes/reportes.api';
import type { TurnoConCuadreOpcional, TurnoLiquidacion } from '@/features/reportes/reportes.api';

import {
  turnoApi,
  useEstadoBombasTurno,
  useCambiarOperacionBomba,
  useHistorialTurnos,
  useMutarTurno,
  useTurnoActual,
} from '../turno.api';

/** Mensaje legible de un error de la API. */
function mensajeError(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;

  return porDefecto;
}

/** Lista de lados legible: «1, 2, 3 y 4». */
function listarLados(lados: number[]): string {
  const etiquetas = lados.map((lado) => String(lado));

  if (etiquetas.length <= 1) return etiquetas[0] ?? '';

  return `${etiquetas.slice(0, -1).join(', ')} y ${etiquetas[etiquetas.length - 1]}`;
}

function etiquetaEstadoBomba(tipo: string): {
  etiqueta: string;
  variante: 'success' | 'warning' | 'danger' | 'neutral';
} {
  switch (tipo) {
    case 'PumpFillingStatus':
      return { etiqueta: 'Despachando', variante: 'success' };
    case 'PumpEndOfTransactionStatus':
      return { etiqueta: 'Despacho terminado', variante: 'warning' };
    case 'PumpIdleStatus':
      return { etiqueta: 'Disponible', variante: 'neutral' };
    case 'PumpOfflineStatus':
      return { etiqueta: 'Sin conexión', variante: 'danger' };
    default:
      return { etiqueta: 'Estado no reconocido', variante: 'warning' };
  }
}

/** Color del punto que acompaña al estado en vivo del lado. */
function puntoEstado(variante: string | undefined): string {
  switch (variante) {
    case 'success':
      return 'bg-emerald-500';
    case 'warning':
      return 'bg-amber-500';
    case 'danger':
      return 'bg-rose-500';
    default:
      return 'bg-ink-300';
  }
}

/**
 * Mi turno (`/turno`).
 *
 * Puerto de la pantalla del grifero del panel anterior (`Turno::index`), con el
 * mismo cuadre que ve el supervisor en *Reportes · Turnos*: aquí el operador abre su
 * turno, lo consulta, registra bóvedas y gastos, corrige cobros y lo cierra.
 */
export function TurnoPage() {
  useDocumentTitle('Turno · Mi turno');

  const consulta = useTurnoActual();
  const navegar = useNavigate();
  /** El estado del controlador sólo se consulta con el turno abierto (antes confunde). */
  const estadoBombas = useEstadoBombasTurno(Boolean(consulta.data?.abierto));
  const cambiarOperacionBomba = useCambiarOperacionBomba();
  const permisos = usePermisosLiquidacion();
  const mutaciones = useMutarTurno();

  const [dineroLlegada, setDineroLlegada] = useState('0');
  const [confirmarApertura, setConfirmarApertura] = useState(false);
  const [bombaPorCambiar, setBombaPorCambiar] = useState<{
    bomba: number;
    estado: 'On' | 'Off';
  } | null>(null);
  /** Detalle del cuadre del turno abierto. */
  const [calculosAbierto, setCalculosAbierto] = useState(false);
  /** Turno cuyas bóvedas y gastos se están gestionando (el abierto o uno ya cerrado). */
  const [ajustesDe, setAjustesDe] = useState<TurnoConCuadreOpcional | null>(null);
  const [fechaHistorial, setFechaHistorial] = useState(() => new Date().toISOString().slice(0, 10));
  const [aviso, setAviso] = useState<{ tono: 'success' | 'error'; texto: string } | null>(null);

  const historial = useHistorialTurnos(fechaHistorial);

  const datos = consulta.data;
  const turno = datos?.turno ?? null;
  const resumen = turno?.resumen_financiero ?? null;
  /**
   * Cierre a ciegas (`config.closure`): el cuadre y los contómetros se esconden para que el
   * operador no pueda calcular la venta antes de entregar el efectivo. La API tampoco los
   * manda (llegan en `null`), así que aquí sólo se explica.
   */
  const aCiegas = Boolean(datos?.cierre_a_ciegas);
  /**
   * Ventas del turno todavía sin comprobante. El cierre las boletea automáticamente y no deja
   * cerrar mientras quede alguna, así que se avisa aquí antes de que el operador lo intente.
   */
  const libres = datos?.transacciones_libres ?? 0;

  const abrirTurno = () => {
    setAviso(null);

    mutaciones.abrir.mutate(
      { dinero_llegada: Number(dineroLlegada || 0) },
      {
        onSuccess: (respuesta) => {
          setConfirmarApertura(false);

          const asignados =
            respuesta.lados.length === 0
              ? 'No tienes lados programados para este turno: pide al supervisor que te asigne.'
              : `Se te asignaron los lados ${listarLados(respuesta.lados)}.`;
          const ocupados =
            respuesta.lados_ocupados.length === 0
              ? ''
              : ` Los lados ${listarLados(respuesta.lados_ocupados)} ya los tenía otro operador, así que quedaron fuera.`;

          setAviso({
            tono: respuesta.lados.length === 0 || respuesta.lados_ocupados.length > 0 ? 'error' : 'success',
            texto: `Turno abierto (${respuesta.turno_id}). ${asignados}${ocupados}`,
          });
        },
        onError: (error) => {
          setAviso({ tono: 'error', texto: mensajeError(error, 'No se pudo abrir el turno.') });
          // Si la apertura la ganó otra pestaña (o el turno ya estaba abierto), la pantalla
          // se refresca para mostrar el turno que sí quedó abierto.
          void consulta.refetch();
        },
      },
    );
  };

  const confirmarOperacionBomba = () => {
    if (!bombaPorCambiar) return;

    cambiarOperacionBomba.mutate(bombaPorCambiar, {
      onSuccess: (respuesta) => {
        setBombaPorCambiar(null);
        setAviso({ tono: 'success', texto: respuesta.mensaje });
      },
      onError: (error) => {
        setBombaPorCambiar(null);
        setAviso({
          tono: 'error',
          texto: mensajeError(error, 'No se pudo cambiar el estado de la bomba.'),
        });
      },
    });
  };

  const verTicket = async (turnoId: number) => {
    try {
      // Se abre en una pestaña (el mismo PDF que el aviso de cierre), no se descarga.
      await mostrarPdfAutenticado(turnoApi.ticket(turnoId));
    } catch (error) {
      setAviso({
        tono: 'error',
        texto: error instanceof Error ? error.message : 'No se pudo abrir el ticket del turno.',
      });
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Operador</p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
            <Clock className="size-6 text-brand-500" aria-hidden="true" />
            Mi turno
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-ink-500">
            Abre tu turno al empezar, registra las bóvedas y los gastos del día y ciérralo al
            terminar. El cuadre es el mismo que revisa el supervisor en el reporte de turnos.
          </p>
        </div>

        <Button
          variant="outline"
          size="sm"
          className="whitespace-nowrap"
          leftIcon={<RefreshCw className="size-4" aria-hidden="true" />}
          loading={consulta.isFetching}
          onClick={() => void consulta.refetch()}
        >
          Actualizar
        </Button>
      </header>

      {aviso ? (
        <Alert
          variant={aviso.tono}
          title={aviso.tono === 'success' ? 'Listo' : 'No se pudo completar'}
        >
          {aviso.texto}
        </Alert>
      ) : null}

      {turno ? (
        <SectionCard
          title="Estado en vivo de mis bombas"
          description="Lectura directa del controlador PTS; se actualiza automáticamente cada 3 segundos."
          action={
            <Button
              variant="outline"
              size="sm"
              loading={estadoBombas.isFetching}
              onClick={() => void estadoBombas.refetch()}
            >
              <RefreshCw className="mr-1 size-3.5" aria-hidden="true" />
              Actualizar estado
            </Button>
          }
        >
          {estadoBombas.isPending ? (
            <Skeleton className="h-24 w-full" />
          ) : estadoBombas.isError ? (
            <Alert variant="error" title="No se pudo leer el estado de las bombas">
              {mensajeError(estadoBombas.error, 'El controlador PTS no está respondiendo.')}
            </Alert>
          ) : estadoBombas.data.lados.length === 0 ? (
            <Alert variant="warning" title="No hay bombas asignadas">
              Ahora mismo no tienes surtidores a tu nombre. Se cargan al abrir el turno con la
              programación que te haya dejado el supervisor.
            </Alert>
          ) : (
            <>
              <p className="mb-3 text-xs text-ink-500">
                Última lectura: {formatDateTimeValue(estadoBombas.data.actualizado)}
              </p>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {estadoBombas.data.lados.map((bomba) => {
                  const paquete = estadoBombas.data.packets.find(
                    (item) => Number(item.Data?.Pump) === bomba,
                  );
                  const estado = paquete ? etiquetaEstadoBomba(paquete.Type) : null;
                  const datosBomba = paquete?.Data;
                  const despachando = paquete?.Type === 'PumpFillingStatus';
                  const operacion = estadoBombas.data.operaciones?.[bomba];
                  const activa = operacion === 'On';
                  const puedeCambiar =
                    operacion !== null &&
                    operacion !== undefined &&
                    (!despachando || !activa);
                  /** Productos que despacha el lado (sus mangueras del turno). */
                  const productos = [
                    ...new Set(
                      (datos?.contometros_pts ?? [])
                        .filter((lectura) => lectura.bomba === bomba)
                        .map((lectura) => lectura.producto)
                        .filter((producto): producto is string => Boolean(producto)),
                    ),
                  ];

                  return (
                    <div
                      key={bomba}
                      className="rounded-control border border-ink-100 bg-white p-3"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex min-w-0 items-center gap-2.5">
                          <span
                            className="flex size-10 shrink-0 items-center justify-center rounded-control bg-brand-50 text-brand-600"
                            aria-hidden="true"
                          >
                            <Fuel className="size-5" />
                          </span>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-ink-800">
                              Bomba {bomba}
                            </p>
                            <p
                              className="truncate text-xs text-ink-500"
                              title={productos.join(' · ')}
                            >
                              {productos.length > 0
                                ? productos.join(' · ')
                                : 'Sin mangueras asignadas'}
                            </p>
                          </div>
                        </div>
                        <Badge
                          variant={activa ? 'success' : operacion === 'Off' ? 'danger' : 'warning'}
                        >
                          {activa ? 'Activa' : operacion === 'Off' ? 'Suspendida' : 'Sin control'}
                        </Badge>
                      </div>
                      <div className="mt-2.5">
                        <p className="mb-2 flex items-center gap-1.5 text-xs text-ink-600">
                          <span
                            className={`size-2 shrink-0 rounded-full ${puntoEstado(estado?.variante)}`}
                            aria-hidden="true"
                          />
                          {estado?.etiqueta ?? 'Sin respuesta del controlador'}
                        </p>
                        <Button
                          variant={activa ? 'danger' : 'outline'}
                          size="sm"
                          className="mt-2 w-full"
                          aria-label={`${activa ? 'Suspender' : 'Activar'} bomba ${bomba}`}
                          disabled={!puedeCambiar || cambiarOperacionBomba.isPending}
                          onClick={() =>
                            setBombaPorCambiar({ bomba, estado: activa ? 'Off' : 'On' })
                          }
                        >
                          {activa ? (
                            <Pause className="mr-1 size-3.5" aria-hidden="true" />
                          ) : (
                            <Play className="mr-1 size-3.5" aria-hidden="true" />
                          )}
                          {activa ? 'Suspender' : 'Activar'}
                        </Button>
                      </div>
                      {paquete?.Message ? (
                        <p className="mt-2 text-xs text-rose-700">{paquete.Message}</p>
                      ) : null}
                      {despachando && activa ? (
                        <p className="mt-2 text-xs text-amber-700">
                          Espera a que termine el despacho para poder suspenderla.
                        </p>
                      ) : null}
                      {despachando ? (
                        <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                          <span className="text-ink-500">Manguera</span>
                          <span className="text-right font-medium text-ink-700">
                            {datosBomba?.Nozzle ?? '—'}
                          </span>
                          <span className="text-ink-500">Producto</span>
                          <span className="text-right font-medium text-ink-700">
                            {datosBomba?.FuelGradeName ?? 'En despacho'}
                          </span>
                          <span className="text-ink-500">Volumen</span>
                          <span className="text-right font-semibold tabular-nums text-ink-800">
                            {aCiegas ? '*' : formatNumber(Number(datosBomba?.Volume ?? 0))}
                          </span>
                          <span className="text-ink-500">Importe</span>
                          <span className="text-right font-semibold tabular-nums text-ink-800">
                            {aCiegas ? '*' : formatCurrency(Number(datosBomba?.Amount ?? 0))}
                          </span>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </SectionCard>
      ) : null}

      {consulta.isLoading ? (
        <SectionCard title="Tu turno">
          <Skeleton className="h-40 w-full" />
        </SectionCard>
      ) : null}

      {consulta.isError ? (
        <SectionCard title="Tu turno">
          <Alert variant="error" title="No se pudo leer tu turno">
            {mensajeError(consulta.error, 'Vuelve a intentarlo en unos segundos.')}
          </Alert>
        </SectionCard>
      ) : null}

      {datos && !datos.abierto ? (
        <SectionCard
          title="No tienes un turno abierto"
          description={
            datos.ultimo_turno?.hora_salida
              ? `Tu último turno se cerró el ${formatDateTimeValue(datos.ultimo_turno.hora_salida)}.`
              : 'Al abrirlo empieza a contar el cuadre del día.'
          }
        >
          <div className="space-y-4">
            {datos.lados_programados.length > 0 ? (
              <Alert variant="info" title="Tu programación de este turno">
                El supervisor te asignó {listarLados(datos.lados_programados)}. Al abrir el turno
                quedarán a tu nombre y podrás despachar en ellos.
              </Alert>
            ) : (
              <Alert variant="warning" title="No tienes lados programados">
                No hay una programación a tu nombre para este turno. Puedes abrir el turno, pero no
                tendrás surtidores asignados hasta que el supervisor te asigne.
              </Alert>
            )}

            <label className="block max-w-xs text-sm text-ink-600">
              Efectivo con el que empiezas
              <Input
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                className="mt-1"
                value={dineroLlegada}
                onChange={(evento) => setDineroLlegada(evento.target.value)}
              />
              <span className="mt-1 block text-xs text-ink-400">
                Es la cantidad inicial del turno y queda registrada para el cuadre.
              </span>
            </label>

            <details className="rounded-control border border-ink-100 bg-ink-50/40 p-3">
              <summary className="cursor-pointer text-sm font-semibold text-ink-800">
                Lecturas iniciales de las mangueras
              </summary>
              {datos.lecturas_iniciales ? (
                <>
                  <p className="mt-1 text-xs leading-relaxed text-ink-500">
                    Son las lecturas con las que cerró tu turno anterior (#
                    {datos.lecturas_iniciales.turno_id}
                    {datos.lecturas_iniciales.fecha_cierre
                      ? `, ${formatDateTimeValue(datos.lecturas_iniciales.fecha_cierre)}`
                      : ''}
                    ). Se registran solas al abrir el turno: no hay que escribirlas.
                  </p>
                  <div className="mt-3 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-xs uppercase tracking-wide text-ink-500">
                        <tr>
                          <th className="px-2 py-1 text-left font-semibold">Lado / manguera</th>
                          <th className="px-2 py-1 text-left font-semibold">Producto</th>
                          <th className="px-2 py-1 text-right font-semibold">Lectura de cierre</th>
                        </tr>
                      </thead>
                      <tbody>
                        {datos.lecturas_iniciales.lados.map((lectura) => (
                          <tr
                            key={`${lectura.bomba}:${lectura.manguera}`}
                            className="border-t border-ink-100"
                          >
                            <td className="px-2 py-1 text-ink-700">
                              Lado {lectura.bomba} · manguera {lectura.manguera}
                            </td>
                            <td className="px-2 py-1 text-ink-600">
                              {lectura.producto ?? 'Sin producto'}
                            </td>
                            <td className="px-2 py-1 text-right tabular-nums text-ink-800">
                              {lectura.contometro_salida === null
                                ? 'Sin lectura'
                                : formatNumber(lectura.contometro_salida)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <p className="mt-1 text-xs leading-relaxed text-ink-500">
                  Todavía no hay un turno cerrado con lecturas. Al abrir el turno se toman las
                  últimas del controlador (PTS) de cada manguera.
                </p>
              )}
            </details>

            <Button
              leftIcon={<CircleDollarSign className="size-4" aria-hidden="true" />}
              loading={mutaciones.abrir.isPending}
              onClick={() => setConfirmarApertura(true)}
            >
              Abrir turno
            </Button>
          </div>
        </SectionCard>
      ) : null}

      {turno ? (
        <>
          <SectionCard
            title={`Turno #${turno.id} abierto`}
            description={`Al cerrarlo podrá verificarlo el supervisor. Empezaste el ${formatDateTimeValue(turno.hora_llegada)}`}
            action={
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  leftIcon={<Receipt className="size-4" aria-hidden="true" />}
                  onClick={() => navegar('/turno/comprobante')}
                >
                  Crear comprobante
                </Button>
                {/** La verificación es posterior al cierre: en un turno abierto el
                 * supervisor todavía no ha visto nada, así que no se muestra su estado
                 * (antes decía «Pendiente de verificación» y confundía). Se ve en
                 * «Mis turnos», con el turno ya cerrado. */}
                {(resumen?.total_entregado ?? 0) !== 0 ? (
                  <Badge variant="outline">
                    Entregado {formatCurrency(resumen?.total_entregado ?? 0)}
                  </Badge>
                ) : null}
              </div>
            }
          >
            {aCiegas ? (
              <Alert variant="info" title="Cierre a ciegas">
                Los contómetros y el cuadre del turno no se muestran: el supervisor los revisa al
                verificar el turno, cuando ya entregaste el efectivo.
              </Alert>
            ) : (
              <details className="rounded-control border border-ink-100 bg-ink-50/40 p-3">
                <summary className="cursor-pointer text-sm font-semibold text-ink-800">
                  Contómetros de los surtidores asignados
                </summary>
              {datos?.contometros_pts.length ? (
                <div className="mt-3 overflow-x-auto rounded-control border border-ink-100">
                  <table className="w-full text-sm">
                    <thead className="bg-ink-50 text-xs uppercase tracking-wide text-ink-500">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold">Bomba / manguera</th>
                        <th className="px-3 py-2 text-left font-semibold">Producto</th>
                        <th className="px-3 py-2 text-right font-semibold">Inicio</th>
                        <th className="px-3 py-2 text-right font-semibold">Lectura actual</th>
                        <th className="px-3 py-2 text-right font-semibold">Volumen del turno</th>
                      </tr>
                    </thead>
                    <tbody>
                      {datos.contometros_pts.map((lectura) => (
                        <tr
                          key={`${lectura.bomba}:${lectura.manguera}`}
                          className="border-t border-ink-100"
                        >
                          <td className="px-3 py-2 text-ink-700">
                            Bomba {lectura.bomba} · manguera {lectura.manguera}
                          </td>
                          <td className="px-3 py-2 text-ink-600">
                            {lectura.producto ?? 'Sin producto'}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-ink-600">
                            {lectura.contometro_llegada === null
                              ? 'Sin lectura'
                              : formatNumber(lectura.contometro_llegada)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-ink-600">
                            {lectura.contometro_actual === null
                              ? 'Sin lectura'
                              : formatNumber(lectura.contometro_actual)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-ink-800">
                            {lectura.volumen_turno === null
                              ? '—'
                              : formatNumber(lectura.volumen_turno)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-ink-500">
                  No hay mangueras PTS asignadas a tu usuario. Solicita que te asignen los
                  surtidores antes de registrar ventas.
                </p>
              )}
              </details>
            )}
          </SectionCard>

          <div className="grid gap-6 xl:grid-cols-2">
            <SectionCard
              title="Acciones del turno"
              description="Las bóvedas y los gastos del turno, y el detalle de cómo se calculó el cuadre."
            >
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<Settings2 className="size-4" aria-hidden="true" />}
                  onClick={() => setAjustesDe(turno)}
                >
                  Bóvedas y gastos
                </Button>
                {/* Con cierre a ciegas el operador no ve el cuadre: el botón desaparece. */}
                {aCiegas ? null : (
                  <Button
                    variant="outline"
                    size="sm"
                    leftIcon={<Calculator className="size-4" aria-hidden="true" />}
                    onClick={() => setCalculosAbierto(true)}
                  >
                    Ver cálculos
                  </Button>
                )}
              </div>

              <div className="mt-4">
                <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink-800">
                  <Users className="size-4 text-ink-400" aria-hidden="true" />
                  Compañeros del turno
                </h3>
                {datos && datos.companeros.length > 0 ? (
                  <ul className="space-y-1 text-sm text-ink-600">
                    {datos.companeros.map((companero) => (
                      <li key={companero.id} className="flex items-center justify-between gap-3">
                        <span>{companero.nombre || `Usuario ${companero.usuario_id}`}</span>
                        <span className="text-xs text-ink-400">
                          desde {formatDateTimeValue(companero.hora_llegada)}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-ink-500">El turno está a tu nombre.</p>
                )}
              </div>
            </SectionCard>

            <SectionCard
              title="Cerrar turno"
              description={
                aCiegas
                  ? 'El cierre tiene su propia pantalla: procesa las lecturas del controlador y confirma el cierre del turno.'
                  : 'El cierre tiene su propia pantalla: revisa los contómetros, procesa las lecturas del controlador y confirma el cierre con su PDF.'
              }
            >
              <div className="space-y-4">
                {aCiegas ? (
                  <Alert variant="info" title="Antes de cerrar">
                    El cierre es <strong>a ciegas</strong>: el efectivo esperado lo calcula el
                    supervisor al verificar el turno, así que aquí no se muestra.
                  </Alert>
                ) : resumen ? (
                  <Alert variant="info" title="Antes de cerrar">
                    El efectivo esperado es {formatCurrency(resumen.efectivo_calculado)} y llevas
                    entregado {formatCurrency(resumen.total_entregado ?? 0)}.
                  </Alert>
                ) : null}

                {libres > 0 ? (
                  <Alert variant="warning" title="Ventas sin comprobante">
                    Tienes {libres} transacción(es) del turno sin comprobante. Al cerrar el turno
                    se <strong>boletan automáticamente</strong>: no se puede cerrar con ventas sin
                    documento.
                  </Alert>
                ) : null}

                <Button
                  variant="danger"
                  leftIcon={<Lock className="size-4" aria-hidden="true" />}
                  onClick={() => navegar('/turno/cerrar')}
                >
                  Ir a cerrar turno
                </Button>
              </div>
            </SectionCard>
          </div>
        </>
      ) : null}

      {/* El historial sólo se muestra sin turno abierto: durante el turno estorba. */}
      {turno ? null : (
        <SectionCard
          title="Mis turnos"
          description="Historial de tus turnos del día elegido: los que empezaste y los que cerraste ese día."
          action={
            <Input
              type="date"
              className="w-40"
              value={fechaHistorial}
              onChange={(evento) => setFechaHistorial(evento.target.value)}
            />
          }
        >
        {historial.isLoading ? <Skeleton className="h-24 w-full" /> : null}

        {historial.data && historial.data.turnos.length === 0 ? (
          <p className="text-sm text-ink-500">No tienes turnos registrados ese día.</p>
        ) : null}

        {historial.data && historial.data.turnos.length > 0 ? (
          <ul className="space-y-3">
            {historial.data.turnos.map((item) => (
              <li
                key={item.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-control border border-ink-100 p-3"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-ink-800">Turno #{item.id}</span>
                    {item.estado_verificacion === 2 ? (
                      <Badge variant="success">Verificado</Badge>
                    ) : item.estado_verificacion === 3 ? (
                      <Badge variant="danger">Rechazado</Badge>
                    ) : (
                      <Badge variant="warning">Pendiente</Badge>
                    )}
                    {item.hora_salida === null ? <Badge variant="brand">Abierto</Badge> : null}
                  </div>
                  <p className="mt-1 text-xs text-ink-500">
                    {formatDateTimeValue(item.hora_llegada)} →{' '}
                    {item.hora_salida ? formatDateTimeValue(item.hora_salida) : 'en curso'}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {item.hora_salida === null ? (
                    <Badge variant="outline">
                      <AlertTriangle className="mr-1 size-3.5" aria-hidden="true" />
                      Sin cerrar
                    </Badge>
                  ) : (
                    <>
                      {/* Cerrado y sin verificar: todavía se puede ingresar la última
                          bóveda (el efectivo depositado al terminar) o un gasto. */}
                      {item.estado_verificacion !== 2 && item.estado_verificacion !== 3 ? (
                        <Button
                          variant="outline"
                          size="sm"
                          leftIcon={<Settings2 className="size-4" aria-hidden="true" />}
                          onClick={() => setAjustesDe(item)}
                        >
                          Bóvedas y gastos
                        </Button>
                      ) : null}
                      {/* Con cierre a ciegas no se entrega el PDF del cuadre (la API
                          también lo rechaza): lo revisa el supervisor al verificar. */}
                      {aCiegas ? null : (
                        <Button
                          variant="outline"
                          size="sm"
                          leftIcon={<Printer className="size-4" aria-hidden="true" />}
                          onClick={() => void verTicket(item.id)}
                        >
                          Ticket
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : null}
        </SectionCard>
      )}

      <ConfirmDialog
        open={confirmarApertura}
        title="Abrir turno"
        description={`Se abre tu turno con ${formatCurrency(Number(dineroLlegada || 0))} de efectivo inicial. Las lecturas de llegada se toman del cierre de tu turno anterior.`}
        confirmLabel="Abrir turno"
        tone="primary"
        loading={mutaciones.abrir.isPending}
        onClose={() => setConfirmarApertura(false)}
        onConfirm={abrirTurno}
      />

      <ConfirmDialog
        open={bombaPorCambiar !== null}
        title={bombaPorCambiar?.estado === 'Off' ? 'Suspender bomba' : 'Activar bomba'}
        description={
          bombaPorCambiar?.estado === 'Off'
            ? `La bomba ${bombaPorCambiar.bomba} dejará de aceptar nuevos despachos.`
            : `La bomba ${bombaPorCambiar?.bomba ?? ''} volverá a aceptar despachos.`
        }
        confirmLabel={bombaPorCambiar?.estado === 'Off' ? 'Suspender bomba' : 'Activar bomba'}
        tone={bombaPorCambiar?.estado === 'Off' ? 'danger' : 'primary'}
        loading={cambiarOperacionBomba.isPending}
        onClose={() => setBombaPorCambiar(null)}
        onConfirm={confirmarOperacionBomba}
      />

      <TurnoAjustesDialog
        turno={ajustesDe}
        permisos={permisos.data}
        onClose={() => setAjustesDe(null)}
        onCambio={async () => {
          await Promise.all([consulta.refetch(), historial.refetch()]);
        }}
      />
      {/* El modal del cuadre sólo se abre con **cierre informado** (su botón se oculta con
          el cierre a ciegas), así que aquí el turno siempre trae su `resumen_financiero`. */}
      <TurnoCalculosDialog
        turno={calculosAbierto && !aCiegas ? (turno as TurnoLiquidacion | null) : null}
        onClose={() => setCalculosAbierto(false)}
      />
    </div>
  );
}
